import { alpha, ButtonBase, SxProps, Theme, useTheme } from '@mui/material';
import { ConversationChannelTypeExtended } from '@provision/chat-utils';
import { useCallback, useMemo } from 'react';
import { DynamicIcon } from 'src/components';
import { SelectMenu, SelectMenuOption } from 'src/components/SelectMenu';

const INTERNAL_NOTE_ICON = 'sticky-note';
const PUBLIC_REPLY_ICON = 'message-square';

const getMessageTypeLucideIcon = (value: ConversationChannelTypeExtended): string =>
  value === ConversationChannelTypeExtended.InternalNote
    ? INTERNAL_NOTE_ICON
    : PUBLIC_REPLY_ICON;

const getMessageTypeTriggerSx = (theme: Theme, open: boolean): SxProps<Theme> => ({
  alignSelf: 'flex-start',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.4375rem',
  padding: '0.1875rem 0.4rem',
  borderRadius: '0.375rem',
  fontFamily: 'inherit',
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  '& .MuiIcon-root, & svg': { fontSize: '1rem' },
  '&:hover': { backgroundColor: alpha(theme.palette.text.primary, 0.08) },
  ...(open && { backgroundColor: alpha(theme.palette.text.primary, 0.08) }),
});

interface MessageTypeOption {
  label: string;
  value: ConversationChannelTypeExtended;
}

export interface MessageTypeSelectorProps {
  disabled?: boolean;
  selectedValue: string;
  onMessageTypeChange: (value: ConversationChannelTypeExtended) => void;
  options: MessageTypeOption[];
}

const MessageTypeSelector = ({
  disabled,
  selectedValue,
  options,
  onMessageTypeChange,
}: MessageTypeSelectorProps) => {
  const theme = useTheme();

  const selectOptions = useMemo<SelectMenuOption[]>(
    () =>
      options.map((option) => ({
        value: option.value,
        title: option.label,
        icon: (
          <DynamicIcon
            name={getMessageTypeLucideIcon(option.value)}
            library="lucide"
            size={15}
          />
        ),
      })),
    [options],
  );

  const handleChange = useCallback(
    (value: string) => {
      onMessageTypeChange(value as ConversationChannelTypeExtended);
    },
    [onMessageTypeChange],
  );

  return (
    <SelectMenu
      disabled={disabled}
      label="Message type"
      options={selectOptions}
      value={selectedValue}
      onChange={handleChange}
      plainOptionIcons
      menuWidth="12.5rem"
      renderTrigger={({ open, selectedOption, getTriggerProps }) => (
        <ButtonBase
          {...getTriggerProps()}
          disabled={disabled}
          data-testid="message-type-selector"
          sx={getMessageTypeTriggerSx(theme, open)}
        >
          {selectedOption?.icon}
          {selectedOption?.title}
          <DynamicIcon
            name="chevron-down"
            library="lucide"
            size={14}
            sx={{
              color: theme.palette.custom.OV.sys.onSurfaceVariant,
              lineHeight: 1,
            }}
          />
        </ButtonBase>
      )}
    />
  );
};

export default MessageTypeSelector;
